<template>
  <div id="chat-component">
    <ul id="chat-tab-buttons" class="nav nav-tabs">
      <li :class="{active: currentTab === tabs.CHAT}">
        <a @click="currentTab = tabs.CHAT">Chat</a>
      </li>
      <li :class="{active: currentTab === tabs.PARTICIPANTS}">
        <a @click="currentTab = tabs.PARTICIPANTS">Participants ({{ numActive }})</a>
      </li>
      <li v-if="isManager"
          :class="{active: currentTab === tabs.SETTINGS}"
          class="tabbed-icon"
      >
        <a @click="currentTab = tabs.SETTINGS"
           class="manager-tab"
           title="Chat Moderation (Manager-only)"
        >
          <Admin/>
        </a>
      </li>
    </ul>
    <div v-if="currentTab === tabs.CHAT"
         id="chat-tab-msg"
         :class="{'chat-closed': socketClosed}"
    >
      <LoadingSpinnerOverlay v-if="socketConnecting" />

      <div id="chat-component-log">
        <div class="chat-component-log-start" v-if="isComplete">Start of conversation.</div>
        <div class="chat-component-log-previous" v-if="!isComplete && messages.length"
          title="Load message history"
          @click="clickHistory">Load previous...</div>

        <ChatMessage v-for="(message, index) in messages" :key="message.message_id"
                     :message="message"
                     :prevMessage="messages[index - 1]"
                     :status="statuses[message.sender_id]"
                     :myId="myId"
                     :isFirstNew="firstNewMessageId == message.message_id"
                     :isModerating="isModerating"
                     :canBan="canBan"
                     :timezone="timezone"
                     @delete="sendDelete(message.message_id)"
                     @ban="sendBan"
        />
      </div>

      <!-- Error block -->
      <ChatError v-if="error" :error="error" />

      <form class="chat-component-message-widget-v2">
                <textarea v-model="message"
                          :disabled="!canMessage"
                          v-on:keyup.enter.exact="sendMessage"
                          @change="updateDraft"
                          :placeholder="socketClosed ? 'Chat closed' : 'Type a message...'"
                          name="chat-input" 
                          rows="3"></textarea>
        <button @click="sendMessage"
                :disabled="!canSubmit"
                id="chat-component-message-submit"
                class="btn btn-primary btn-large"
                type="button"
        >Send
        </button>
      </form>
    </div>
    <div v-if="currentTab === tabs.PARTICIPANTS"
         id="chat-tab-status">
      <ChatPresence v-for="(presence, index) in participantsActive" :key="index"
                          :presence="presence" :isOnline="true"
      />
      <div class="chat-status-absent">
        <ChatPresence v-for="(presence, index) in participantsGone" :key="index"
                          :presence="presence" :isOnline="false"
        />
      </div>
      <div class="chat-status-banned">
      </div>
    </div>
    <div v-if="isManager && currentTab === tabs.SETTINGS"
         id="chat-tab-settings">
      <div class="chat-tab-inner">
        <h5 class="text">Chat Moderation (Manager-only)</h5>
        <div class="moderation-block">
          <h4>Enable Moderation</h4>
          <p>Allows you to delete individual chat messages. Defaults to 'OFF' for safety.</p>

          <SwitchLegacy
              @click="isModerating = !isModerating"
              :value="isModerating"
              :title="'Enable moderation'"
          />
        </div>
        <div class="moderation-block">
          <h4>Wipe all messages</h4>
          <p>This will wipe the <b>entire</b> message history of
            <b>{{ messages.length }}{{ messages.length > 50 ? '+' : '' }}</b>
            messages for this chat.</p>
          <p>You almost certainly don't want to do this, unless you're resetting the display after testing.</p>

          <ButtonWithInlineConfirmation v-if="messages.length"
                                        @click="sendDeleteAll">
            <template #button>Wipe chat...</template>
            <template #confirm>Yes - Wipe all</template>
          </ButtonWithInlineConfirmation>
          <button v-else class="btn btn-danger disabled">Wipe chat...</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';

import ButtonWithInlineConfirmation from "@/components/shared/ButtonWithInlineConfirmation";
import ChatError from "./ChatError"
import ChatMessage from "./ChatMessage";
import ChatPresence from "./ChatPresence";
import EventService from "../../services/EventService";
import LoadingSpinnerOverlay from '@/components/LoadingSpinnerOverlay';
import Admin from "@/components/icons/Admin";
import SwitchLegacy from "@/components/shared/SwitchLegacy";
import {compareBy} from "../Utils";

/* SCROLL LOGIC
 * On initial page load, scroll to the end of the message thread
 * If new messages arrive, the page should auto-scroll to stay at the end of thread
 * If old history arrives, the page should auto-scroll to stay at the end of thread
 * If the user starts scrolling, we set isScrolling = True
 *   * In this state, auto-scroll is disabled
 *   * If they scroll back to the end, we reset isScrolling = false
 */

/* HAS MESSAGE BEEN READ? (updateReadPointer)
 * If pointer is up to date, we can ignore.
 * 
 * Triggers:
 * - On initial page load? NO
 * - If scrollposition=end AND user sends a message? NO
 * - If scrollposition=end AND document.focus=true ...transition? NO
 * - Poll every 10 seconds and check (scrollposition=end AND document.focus=true) YES
 */

export const tabs = {
  CHAT: 'chat',
  PARTICIPANTS: 'participants',
  SETTINGS: 'settings',
};


export default {
  name: "ChatSocketComponent",
  components: {
    Admin,
    ButtonWithInlineConfirmation,
    ChatError,
    ChatMessage,
    ChatPresence,
    LoadingSpinnerOverlay,
    SwitchLegacy,
  },
  props: {
    attendee_id: {
      type: Number,
      default: null
    },
    room: {
      type: String,
    },
    canBan: {
      type: Boolean,
      default: false
    },
    timezone: {
      type: String,
      required: true,
    },
  },
  data: function () {
    return {
      statuses: {},
      message: '',
      isDeleted: false,
      isModerating: false,
      isScrolling: false, // When scrolling, the jumpToBottom logic is disabled
      currentTab: tabs.CHAT,
      intervalCheckRead: null,
    }
  },
  computed: {
    myId() {
      return EventService.jwtPayloadAttendeeId();
    },
    canMessage() {
      if (!this.socketConnected || this.isDeleted || this.error) {
        return false;
      } else {
        return true;
      }
    },
    canSubmit() {
      return (
        this.canMessage && 
        (this.message.length > 0)
      );
      // if (!this.socketConnected || this.isDeleted || this.error) {
      //   return false;
      // }

      // return this.message.length > 0;
    },
    participantsActive() {
      let matches = [];
      Object.values(this.presences).forEach(presence => {
        if (this.isAttendeeOnline(presence.attendee_id)) {
          matches.push(presence);
        }
      });
      return matches.sort(compareBy('name'));
    },
    participantsGone() {
      let matches = [];
      Object.values(this.presences).forEach(presence => {
        if (!this.isAttendeeOnline(presence.attendee_id)) {
          matches.push(presence);
        }
      });
      return matches.sort(compareBy('name'));
    },
    participantsBanned() {
      return [];
    },
    numActive() {
      return this.participantsActive.length;
    },
    messages() {
      return this.messagesByRoom(this.room);
    },
    isReady() {
      return this.isRoomReady(this.room);
    },
    isComplete() {
      return this.isHistoryComplete(this.room);
    },
    presences() {
      return this.presencesByRoom(this.room);
    },
    draft() {
      return this.draftByRoom(this.room);
    },
    error() {
      // Error message dict from Django
      return this.errorByRoom(this.room);
    },
    firstNewMessageId() {
      for (let x=0; x<this.messages.length; x++) {
        if (this.messages[x].message_id > this.lastReadPointer) {
          return this.messages[x].message_id;
        }
      }
      return null;
    },
    lastMessageId() {
      if (this.messages.length) {
        return this.messages[this.messages.length -1].message_id;
      }
      return 0;
    },
    lastReadPointer() {
      return this.myPointersByRoom[this.room];
    },
    socketConnecting() {
      return this.socketState === WebSocket.CONNECTING;
    },
    socketConnected() {
      return this.socketState === WebSocket.OPEN;
    },
    socketClosed() {
      return this.socketState === WebSocket.CLOSED;
    },
    
    ...mapGetters({
      me: 'currentAttendee',
      isManager: 'isManager',
      socketState: 'messages/socketState',
      socketError: 'messages/socketError',
      messagesByRoom: 'messages/byRoom',
      presencesByRoom: 'messages/presencesByRoom',
      draftByRoom: 'messages/draftByRoom',
      errorByRoom: 'messages/errorByRoom',
      myPointersByRoom: 'messages/myPointers',
      isRoomReady: 'messages/isReady',
      isHistoryComplete: 'messages/isHistoryComplete',
      isAttendeeOnline: 'messages/isAttendeeOnline',
    }),
  },
  methods: {
    sendMessage() {
      if (this.message) {
        this.socketSendMessage({room: this.room, message: this.message});
        this.message = '';
        this.updateDraft(); // Clear the draft cache
      }
    },
    sendReadPointer(messageId) {
      this.socketSendReadPointer({room: this.room, messageId: messageId});
    },
    sendDelete(messageId) {
      this.socketSendDelete({room: this.room, messageId: messageId});
    },
    sendDeleteAll() {
      this.socketSendDeleteAll({room: this.room});
    },
    sendBan(attendeeId) {
      this.socketSendBan({room: this.room, attendeeId: attendeeId});
    },
    updateReadPointer() {
      // Should only be triggered when not scrolling
      // If this tab is active, assume the message has been read
      // If we don't have a lastReadPointer, this is the first time we've viewed this chat
      if (this.isScrolling) { return; }

      if (document.hasFocus() && this.lastMessageId) {
        if (!this.lastReadPointer || (this.lastReadPointer < this.lastMessageId)) {
          console.log("Sending pointer update:", this.lastMessageId);
          this.sendReadPointer(this.lastMessageId);
        }
      }
    },
    updateDraft() {
      this.updateDraftByRoom({room: this.room, draft: this.message});
    },
    scrollToBottom() {
      if (this.isScrolling) { return; }

      const objDiv = document.getElementById("chat-component-log");
      if (objDiv) {
        objDiv.scrollTop = objDiv.scrollHeight;
      }
    },
    clickHistory() {
      this.socketSendHistory(this.room);
    },
    afterScroll(scrollable) {
      // Function called WHENEVER scrolling ends
      // BUT scrolling may not be triggered on short message threads or long panes!

      if (scrollable.scrollTop === 0) {
        if (!this.isComplete) {
          this.socketSendHistory(this.room);
        }
        
      } else {
        let offset = scrollable.scrollHeight - scrollable.offsetHeight -5; // 5px leeway
        if (scrollable.scrollTop >= offset) {
          this.isScrolling = false;
        }
      }
      
    },
    ...mapActions({
      socketConnectAndJoin: 'messages/connectAndJoin',
      socketSendJoin: 'messages/sendJoin',
      socketSendMessage: 'messages/sendMessage',
      socketSendReadPointer: 'messages/sendReadPointer',
      socketSendHistory: 'messages/sendHistory',
      socketSendDelete: 'messages/sendDelete',
      socketSendDeleteAll: 'messages/sendDeleteAll',
      socketSendBan: 'messages/sendBan',
      updateDraftByRoom: 'messages/updateDraftByRoom',
    }),
  },
  /* Onload
    * - Ensure we have a socket connection
    * - Add scroll-watching logic
    * - Add read-pointer logic
    * - Recover draft message content
    * - Force a scrollToBottom 
    *   - which might not be triggered when switching back to an existing loaded tab (i.e. one that won't need to update)
    */
  mounted: function () {
    this.socketConnectAndJoin({me: this.me, room: this.room});

    const objDiv = document.getElementById("chat-component-log");
    let scrollTimeout;

    objDiv.addEventListener('scroll', () => {
      window.clearTimeout(scrollTimeout);
      this.isScrolling = true;
      scrollTimeout = setTimeout(this.afterScroll(objDiv), 66); // Trigger 66ms after scrolling ends
    });

    // Check read pointers every ~10~3 seconds
    this.intervalCheckRead = setInterval(this.updateReadPointer, 3000);
    this.message = this.draft;

    this.$nextTick(this.scrollToBottom());
  },
  updated: function () {
    this.$nextTick(this.scrollToBottom());
  },
  created() {
    this.tabs = tabs;
  },
  unmounted: function () {
    clearInterval(this.intervalCheckRead);
  },
}
</script>

<style>

#chat-component {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  padding-top: 5px;
  padding-bottom: 0;
}

#chat-tab-buttons {
  margin-bottom: 0;
}

#chat-tab-buttons a {
  cursor: pointer;
}

#chat-tab-msg {
  position: relative;
}
#chat-tab-msg,
#chat-tab-status,
#chat-tab-settings {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background-color: #fff;
  border-left: 1px solid #ddd;
  height: calc(100% - 40px); /* ~height of chat-tab-buttons */
}

#chat-tab-msg.chat-closed {
  background-color: #ccc;
  color: #999;
}

#chat-tab-status {
  overflow-y: auto;
}

#chat-tab-settings {
  background-color: #000;
  color: #fff;
}

.chat-tab-inner {
  padding: 5px;
}

#chat-component-log {
  box-sizing: border-box;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  padding: 10px;
  overflow-y: auto;
  height: calc(100% - 80px); /* 60px form widget + 10px padding */
}

form.chat-component-message-widget-v2 {
  align-items: stretch;
  background-color: #ddd;
  display: flex;
  margin: 0;
  padding: 10px;
  border-top: 2px solid #999;
}

.chat-component-message-widget-v2 textarea {
  margin-bottom: 0;
  flex-grow: 1;
}

#chat-component .tabbed-icon svg {
  height: 20px;
  width: 20px;
}

#chat-component a.manager-tab {
  color: #000;
  max-height: 20px;
}

#chat-component .nav > li > a.manager-tab:hover {
  background-color: #111;
  color: #eee;
}

#chat-component .nav-tabs > .active a.manager-tab {
  background-color: #000;
  color: #fff;
}

#chat-component .moderation-block {
  border-top: 1px solid #666;
  margin-bottom: 30px;
  padding: 10px;
}

.chat-component-log-start {
  border-bottom: 1px dashed #ccc;
  font-size: 10px;
  margin-bottom: 10px;
  text-align: center;
}
.chat-component-log-previous {
  border-bottom: 1px dashed #ccc;
  font-size: 10px;
  margin-bottom: 10px;
  text-align: center;
  cursor: pointer;
}
</style>